<template>
  <div id="app">
    <spinner :visible="showSpinner"></spinner>
    <modal-router-view />
    <router-view />
    <vue-snotify></vue-snotify>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { http, setHeader } from "@/services/http";
import signifyLogo from "@/assets/img/SIGNIFY_LOGO_RGB@2x.png";
import { REGIONS } from "@/service/Constants";
import DefaultRegionModal from "@/components/modals/DefaultRegionModal";
import settings from "@/service/Settings";
import jwt_decode from "jwt-decode";
// import { User } from "oidc-client-ts";

export default {
  data() {
    return {
      signifyLogo: signifyLogo,
      EMEA_SSO: settings.PORTAL_EMEA_SSO_URL,
      NAM_SSO: settings.PORTAL_NAM_SSO_URL,
      TOOLS_SSO: settings.PORTAL_TOOLS_SSO_URL,
      environment: settings.ENV,
    };
  },
  modals: {
    DefaultRegionModal,
  },
  created() {
    setHeader("Client", "EDITv2");

    this.$store.dispatch("brain/auth/oidcSetup");

    this.oidcGetUser().then((u) => {
      if (u === null) {
        return;
      }
      if (u.expired) {
        this.$store.dispatch("brain/auth/oidcSignoutRedirect");
        return;
      }
      this.$store.dispatch("brain/auth/setOidcUser", u);

      const solutionsString = localStorage.getItem("solutions");
      if (solutionsString && JSON.parse(solutionsString).solutions.length) {
        const solutions = JSON.parse(solutionsString);
        this.$store
          .dispatch("brain/switchRegion", {
            region: solutions.solutions[0].region,
            clearSolution: false,
          })
          .then(() => {
            this.$store.commit(
              "brain/solutions/setActiveSolution",
              solutions.active
            );
            this.$store.commit(
              "brain/solutions/setSolutionNumber",
              solutions.solutionNumber
            );
            this.$store.commit(
              "brain/solutions/setSolutions",
              solutions.solutions
            );
            this.$store.dispatch(
              "brain/solutions/handleActiveSolution",
              solutions.active
            );
          });
      } else {
        this.handleDefaultRegion();
      }
    });

    // Handle OIDC callbacks

    // The following is a workaround to redirect to the correct callback handling pages,
    // since the OpenID provider does not let us use # in redirect URLs.

    // Gather all parameters, and make sure we don't have a trailing `#/`, which is added
    // automatically by the router.
    let searchParams = new URL(window.location.href.replace("#/", ""))
      .searchParams;
    if (searchParams.size > 0) {
      // These are the parameters we use to trigger the redirection to other pages. If
      // we find one of these then we need to switch to the relevant handler.
      let redirectionParams = ["oidc_callback", "oidc_silent"];

      // This is the list of actual meaningful parameters we must pass to the handling
      // route. We build this list by removing the redirection params from all params.
      let meaningfulParams = {};
      for (let [param, value] of searchParams) {
        if (!redirectionParams.includes(param)) {
          meaningfulParams[param] = value;
        }
      }

      if (searchParams.has("oidc_callback")) {
        // Correct the URL which typically contains the hash at the end. If we don't do
        // this the router will simply use everything as a base and append our query
        // to the front of the `#/`
        window.history.replaceState(
          {},
          "",
          new URL("#/", settings.APP_BASE_URL).toString()
        );
        this.$router.replace({
          path: "/oidc-authenticate",
          query: meaningfulParams,
        });
      }

      if (searchParams.has("oidc_silent_callback")) {
        window.history.replaceState(
          {},
          "",
          new URL("#/", settings.APP_BASE_URL).toString()
        );
        this.$router.replace({
          path: "/oidc-silent",
          query: meaningfulParams,
        });
      }
    }

    // TODO: Reenable for metrics
    // This should be moved to the authentication step
    // const aToken = this.$store.getters["brain/auth/activeToken"];
    // const userData = this.$store.getters["brain/auth/userData"];
    // let companyName = "";
    // if (userData !== null && userData.company !== null) {
    //   companyName = userData.company;
    // }

    // try {
    //   const activeToken = jwt_decode(aToken);
    //   window.dataLayer = window.dataLayer || [];
    //   window.dataLayer.push({
    //     userType: activeToken["userType"], //based on self registered or from Signify SSO
    //     userId: activeToken["uuid"],
    //     userCompany: companyName,
    //     environment: settings.ENV,
    //   });
    // } catch (e) {
    //   console.log("No valid token found.");
    // }

    // If both cookies are set, check cookie expiration date to set latest JWT
    if (
      null !== window.$cookies.get("impersonate") &&
      null !== window.$cookies.get("administer")
    ) {
      const impersonateToken = jwt_decode(window.$cookies.get("impersonate"));
      const administerToken = jwt_decode(window.$cookies.get("administer"));
      if (impersonateToken.exp > administerToken.exp) {
        this.impersonate();
      } else {
        this.administer();
      }
    } else if (null !== window.$cookies.get("impersonate")) {
      this.impersonate();
    } else if (null !== window.$cookies.get("administer")) {
      this.administer();
    }

    // --- END ---
    this.setBodyClasses();
  },
  methods: {
    undo() {
      if (this.showSpinner) {
        console.log("loading, undo not yet available.");
      } else {
        this.$store.dispatch("brain/history/undo");
      }
    },
    setBodyClasses() {
      this.bodyClasses.forEach((item) => document.body.classList.add(item));
    },
    handleDefaultRegion() {
      http({
        method: "get",
        url: `/api/v2/users/profile/settings`,
      }).then((response) => {
        if (
          response.data.data.settings !== null &&
          response.data.data.settings !== "null"
        ) {
          let regionObject = JSON.parse(response.data.data.settings);
          let defaultRegion = Object.keys(REGIONS).find(
            (key) => REGIONS[key].label === regionObject.region
          );
          this.$store.commit("brain/setDefaultRegion", regionObject.region);
          this.$store.dispatch("brain/switchRegion", {
            region: defaultRegion,
            clearSolution: true,
          });
        } else if (!this.isImpersonating) {
          setHeader("Region", this.region.toUpperCase());
          this.$modalRouter.push({
            name: "DefaultRegionModal",
            props: {
              show: true,
              modalTitle: "Select your default region",
              allRegions: this.allRegions,
            },
            model: "show",
            on: { changeRegion: this.changeRegion },
          });
        }
      });
    },
    changeRegion() {
      let defaultRegion = Object.keys(REGIONS).find(
        (key) => REGIONS[key].label === this.defaultRegion
      );
      this.$store.dispatch("brain/switchRegion", {
        region: defaultRegion,
        clearSolution: true,
      });
    },
    impersonate() {
      this.$store.dispatch(
        "brain/auth/handleImpersonate",
        window.$cookies.get("impersonate")
      );
    },
    administer() {
      this.$store.dispatch(
        "brain/auth/handleAdminister",
        window.$cookies.get("administer")
      );
    },
    ...mapActions("brain/auth", ["oidcSetup", "oidcGetUser"]),
  },
  mounted() {
    document.addEventListener("keydown", (e) => {
      if (
        (e.key === "Z" && e.ctrlKey) ||
        (e.key === "Z" && e.metaKey && !e.shiftKey)
      ) {
        this.undo();
      }
    });
  },
  computed: {
    ...mapState("brain", [
      "region",
      "maintenanceMode",
      "allRegions",
      "defaultRegion",
    ]),
    ...mapGetters("brain/auth", [
      "isLoggedIn",
      "activeToken",
      "isImpersonating",
    ]),
    ...mapState("brain/history", ["history"]),
    ...mapGetters("brain/ui", ["showSpinner", "bodyClasses"]),
    ...mapGetters("brain/global", ["showSpinner"]),
  },
  watch: {
    bodyClasses() {
      this.setBodyClasses();
    },
  },
};
</script>

<style lang="scss">
@import "./scss/all";

#app {
  height: 100%;
}
</style>
